/**
 * Global registry of GenericDatepicker instances keyed by input id or unique class.
 */

const LOG_PREFIX = '[GenericDatepicker]';
const RESERVED_CLASSES = new Set(['gdp-input']);

function resolveElement(selector) {
  const $ = window.jQuery;
  if (!selector) return null;
  if (selector instanceof HTMLElement) return selector;
  if (selector?.nodeType === 1) return selector;
  if ($ && selector?.jquery) return selector[0] ?? null;
  if ($ && typeof selector === 'string') return $(selector)[0] ?? null;
  return null;
}

function firstClassName(el) {
  return (
    el.className
      ?.trim()
      .split(/\s+/)
      .filter((name) => name && !RESERVED_CLASSES.has(name))[0] ?? null
  );
}

function escapeClassName(className) {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
    return CSS.escape(className);
  }
  return className.replace(/[^\w-]/g, '\\$&');
}

function countElementsWithClass(className) {
  if (!className) return 0;
  return document.querySelectorAll(`.${escapeClassName(className)}`).length;
}

export class GenericDatepickerRegistry {
  constructor() {
    this._map = new Map();
  }

  /**
   * Register an instance when the target input has an id or a unique class.
   * @returns {string|null} registry key when registered
   */
  register(selector, instance) {
    const el = resolveElement(selector);
    if (!el) return null;

    const id = el.id?.trim();
    if (id) {
      if (this._map.has(id) && this._map.get(id) !== instance) {
        console.warn(`${LOG_PREFIX} Registry key "${id}" overwritten by a new instance.`);
      }
      this._map.set(id, instance);
      return id;
    }

    const className = firstClassName(el);
    if (!className) return null;

    const matchCount = countElementsWithClass(className);
    if (matchCount > 1) {
      console.warn(
        `${LOG_PREFIX} Skipped registry for class "${className}" — ${matchCount} inputs share this class. Add unique id attributes instead.`,
      );
      return null;
    }

    if (this._map.has(className) && this._map.get(className) !== instance) {
      console.warn(`${LOG_PREFIX} Registry key "${className}" overwritten by a new instance.`);
    }
    this._map.set(className, instance);
    return className;
  }

  unregister(key) {
    if (!key) return;
    this._map.delete(key);
  }

  get(key) {
    if (!key) return undefined;
    const normalized = key.startsWith('#') || key.startsWith('.') ? key.slice(1) : key;
    return this._map.get(normalized);
  }

  has(key) {
    if (!key) return false;
    const normalized = key.startsWith('#') || key.startsWith('.') ? key.slice(1) : key;
    return this._map.has(normalized);
  }

  ids() {
    return [...this._map.keys()];
  }

  all() {
    return [...this._map.values()];
  }

  get size() {
    return this._map.size;
  }

  clear() {
    this._map.clear();
  }
}

export const genericDatepickerInstances = new GenericDatepickerRegistry();
